import { createFileRoute, Link } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { formatUsd, useCurrency } from "@/lib/currency";
import { useI18n } from "@/lib/i18n";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import {
  History,
  CreditCard,
  Activity,
  LogOut,
  Wallet,
  Mail,
  User as UserIcon,
  Calendar,
  Settings2,
  Share2,
} from "lucide-react";

import { useReferralMe } from "@/lib/queries";

export const Route = createFileRoute("/_app/profile")({
  component: ProfilePage,
});

function ProfilePage() {
  const { user, logout } = useAuth();
  const { t } = useI18n();
  const { format } = useCurrency();
  const [activations, setActivations] = useState<any[]>([]);
  const [recharges, setRecharges] = useState<any[]>([]);
  const [mlpEnabled, setMlpEnabled] = useState(() => localStorage.getItem("mlp_mode") === "1");
  const { data: referralInfo } = useReferralMe();

  const toggleMlp = (val: boolean) => {
    setMlpEnabled(val);
    localStorage.setItem("mlp_mode", val ? "1" : "0");
    // Tells the root to attach or detach its keydown listener right away, instead of
    // keeping one registered on every page just in case.
    window.dispatchEvent(new Event("mlp_mode_changed"));
  };

  useEffect(() => {
    const fetchHistory = async () => {
      // One failure must not blank the other panel, hence allSettled.
      const [activationsResult, rechargesResult] = await Promise.allSettled([
        api.get<any[]>("/api/users/me/history/activations"),
        api.get<any[]>("/api/users/me/history/recharges"),
      ]);
      if (activationsResult.status === "fulfilled") setActivations(activationsResult.value);
      if (rechargesResult.status === "fulfilled") setRecharges(rechargesResult.value);
    };
    fetchHistory();
  }, []);

  if (!user) return null;

  const memberSince = (user as any).created_at
    ? new Date((user as any).created_at).toLocaleDateString()
    : "—";

  return (
    <div className="mx-auto max-w-4xl space-y-8">
      <div>
        <h1 className="text-3xl font-bold tracking-tight">{t("profile.title")}</h1>
        <p className="mt-1 text-muted-foreground">{t("profile.subtitle")}</p>
      </div>

      {/* User info card */}
      <Card className="p-6 border-border bg-card">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div className="flex items-center gap-4">
            <div className="flex h-16 w-16 items-center justify-center rounded-full bg-gradient-primary shadow-glow text-2xl font-bold text-primary-foreground select-none">
              {user.username?.[0]?.toUpperCase() ?? "?"}
            </div>
            <div>
              <p className="text-xl font-bold">{user.username}</p>
              <div className="flex flex-col gap-1 mt-1 text-sm text-muted-foreground">
                <span className="flex items-center gap-1.5">
                  <Mail className="h-3.5 w-3.5" />
                  {user.email}
                </span>
                <span className="flex items-center gap-1.5">
                  <Calendar className="h-3.5 w-3.5" />
                  {t("profile.member_since")}: {memberSince}
                </span>
              </div>
            </div>
          </div>
          <div className="flex flex-col items-end gap-3">
            <div className="flex items-center gap-2 rounded-xl border border-border bg-background px-4 py-2">
              <Wallet className="h-4 w-4 text-primary" />
              <span className="text-sm text-muted-foreground">{t("balance")}:</span>
              <span className="font-bold text-primary">{format(user.balance)}</span>
            </div>
            <div className="flex gap-2">
              <Button asChild size="sm" variant="outline">
                <Link to="/recharge">
                  <CreditCard className="h-4 w-4 mr-1.5" />
                  {t("topup")}
                </Link>
              </Button>
              <Button size="sm" variant="destructive" onClick={logout}>
                <LogOut className="h-4 w-4 mr-1.5" />
                {t("nav.logout")}
              </Button>
            </div>
          </div>
        </div>
      </Card>

      <div className="grid gap-6 md:grid-cols-2">
        <Card className="p-6 border-border bg-card">
          <div className="flex items-center gap-3 mb-4">
            <Activity className="h-5 w-5 text-primary" />
            <h2 className="text-lg font-semibold">{t("profile.activation_history")}</h2>
          </div>
          {activations.length === 0 ? (
            <p className="text-sm text-muted-foreground">{t("profile.no_activations")}</p>
          ) : (
            <ul className="space-y-3">
              {activations.slice(0, 10).map((a) => (
                <li
                  key={a.id}
                  className="flex justify-between items-center text-sm border-b border-border pb-2 last:border-0"
                >
                  <div>
                    <p className="font-medium">{a.service_code?.toUpperCase()}</p>
                    <p className="text-xs text-muted-foreground">
                      {new Date(a.created_at).toLocaleString()}
                    </p>
                  </div>
                  <div className="text-right">
                    <p className="font-mono text-xs">{a.number || t("common.pending")}</p>
                    <p className="text-xs font-semibold text-primary">{format(a.cost)}</p>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </Card>

        <Card className="p-6 border-border bg-card">
          <div className="flex items-center gap-3 mb-4">
            <History className="h-5 w-5 text-primary" />
            <h2 className="text-lg font-semibold">{t("profile.recharge_history")}</h2>
          </div>
          {recharges.length === 0 ? (
            <p className="text-sm text-muted-foreground">{t("profile.no_recharges")}</p>
          ) : (
            <ul className="space-y-3">
              {recharges.slice(0, 10).map((r) => (
                <li
                  key={r.id}
                  className="flex justify-between items-center text-sm border-b border-border pb-2 last:border-0"
                >
                  <div>
                    <p className="font-medium capitalize">{r.status}</p>
                    <p className="text-xs text-muted-foreground">
                      {new Date(r.created_at).toLocaleString()}
                    </p>
                  </div>
                  <div className="font-semibold text-green-500">+{format(r.amount)}</div>
                </li>
              ))}
            </ul>
          )}
        </Card>
      </div>

      {/* Referral programme lives on its own page — this is just the way in. */}
      {referralInfo && (
        <Card className="p-6 border-border bg-card">
          <div className="flex flex-wrap items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              <Share2 className="h-5 w-5 text-primary" />
              <div>
                <h2 className="text-lg font-semibold">{t("profile.referral_title")}</h2>
                <p className="text-sm text-muted-foreground">
                  {t("referral.page_subtitle", {
                    // Deliberately not `format`: the reward is a USD promise, see formatUsd.
                    amount: formatUsd(referralInfo.reward_amount ?? 1),
                    min: formatUsd(referralInfo.referral_min_recharge ?? 3),
                  })}
                </p>
              </div>
            </div>
            <Button asChild variant="outline">
              <Link to="/referrals">{t("referral.open_page")}</Link>
            </Button>
          </div>
        </Card>
      )}

      {/* Settings / Easter egg section */}
      <Card className="p-6 border-border bg-card">
        <div className="flex items-center gap-3 mb-5">
          <Settings2 className="h-5 w-5 text-primary" />
          <h2 className="text-lg font-semibold">{t("profile.settings_title")}</h2>
        </div>
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm font-medium">{t("profile.mlp_mode_title")}</p>
            <p className="text-xs text-muted-foreground mt-0.5">{t("profile.mlp_mode_desc")}</p>
          </div>
          <Switch id="mlp-toggle" checked={mlpEnabled} onCheckedChange={toggleMlp} />
        </div>
      </Card>
    </div>
  );
}
